import { useId } from "react";

/**
 * 할로윈 2026 띠배너 좌우 장면 (#6061 H5) — Figma `halloween-2026-strip-scene-left` / `-right` (300×40).
 *
 * 디자인 핸드오프의 장식 스크립트를 그대로 옮긴 인라인 SVG 다(이미지로 두면 달무리 · 창문 불빛이 움직이지 않는다).
 * 움직이는 요소는 CSS 클래스로 표시한다 — `hw26-moonglow`(달무리) · `hw26-win`(창문 불빛).
 *
 * ```
 *   왼쪽   보름달 · 유령의 집(창문 불빛) · 마른 나무 · 울타리 · 박쥐
 *   오른쪽 초승달 · 마른 나무(거미) · 묘비 위 고양이 · 십자가 · 작은 묘비 · 박쥐
 * ```
 *
 * gradient · mask id 는 `useId` 로 띠마다 다르게 — 데스크톱/모바일을 같이 그려도 겹치지 않는다.
 */

const C = { glow: "#FF9D57", ink: "#000000", star: "#F8E6A0" };

const HOUSE =
  "M58 40V24.5L54.5 24.5 63 17V12.5L60 12.5 65.5 2.5 71 12.5H68V15.5L77.5 8.5 87 15.5V11.5H84.5L91.5 0 98.5 11.5H96V20L100.5 24.5H97V40Z";
const TREE_L =
  "M13 40L14.4 24 9 16.5 10.2 15.6 14.9 21.6 15.4 14 11.8 7.8 13 7.1 16.4 12.6 18.9 6.2 20.3 6.8 17.4 15.2 17.1 21.2 21.9 15.1 23.1 16 17.3 24.4 18 40Z";
const TREE_R =
  "M262 40L263.6 23 255.5 16.5 252 17.8 251.4 16.8 254.8 15.4 249 10.4 250 9.4 256.4 14.6 258.6 8.6 260 9 258 15.6 263.8 20.2 264.6 9.6 260.6 3.8 261.8 3 265.4 8.2 268.4 1.8 269.8 2.4 266.2 10.4 266 20.4 271.6 13.8 277.8 12.2 278.2 13.6 272.6 15 266.8 22 268 40Z";
const STONE = "M190 40V27Q190 21.5 196 21.5Q202 21.5 202 27V40Z";
const CROSS =
  "M212.6 40V27.6H208.4V25H212.6V21.4H215.4V25H219.6V27.6H215.4V40Z";
const CAT =
  "M193.4 21.8V17.4Q192 14.8 192.8 12.4L192 9.2 194 11Q196 10.4 198 11L200 9.2 199.2 12.4Q200.2 14.8 198.8 17.4 200.8 18.8 200 21.8ZM200 21.8Q204.4 22 205 19.2 205.2 17.6 203.6 17 204.6 18.6 203 20.2 201.8 21 200 21Z";
const BAT =
  "M0 .4C-1.2-1.6-3.8-2.4-5.6-1-4.6-.8-4.3 0-4.6 1-3.6.4-2.6.5-2 1.2-1.4.6-.6.7 0 1.3.6.7 1.4.6 2 1.2 2.6.5 3.6.4 4.6 1 4.3 0 4.6-.8 5.6-1 3.8-2.4 1.2-1.6 0 .4Z";

const Bat = ({
  args: [x, y, s, c],
}: {
  args: [number, number, number, string];
}) => <path transform={`translate(${x} ${y}) scale(${s})`} fill={c} d={BAT} />;

export function StripSceneLeft({ className }: { className?: string }) {
  const id = useId().replace(/:/g, "");
  return (
    <svg
      className={`hw26-strip-scene hw26-strip-scene--left hw26-mask ${className ?? ""}`}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 300 40"
      preserveAspectRatio="xMinYMax meet"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <radialGradient
          id={`hw26-sky-l${id}`}
          cx="86"
          cy="13"
          r="78"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#6B47A8" stopOpacity=".75" />
          <stop offset=".45" stopColor="#3E2470" stopOpacity=".45" />
          <stop offset="1" stopColor="#3E2470" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`hw26-moon-glow-l${id}`}>
          <stop offset="0" stopColor={C.glow} stopOpacity=".6" />
          <stop offset="1" stopColor={C.glow} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`hw26-moon-l${id}`} cx=".38" cy=".35" r=".75">
          <stop offset="0" stopColor="#FFE7BF" />
          <stop offset=".55" stopColor="#FFC266" />
          <stop offset="1" stopColor={C.glow} />
        </radialGradient>
      </defs>
      <ellipse cx="86" cy="18" rx="92" ry="40" fill={`url(#hw26-sky-l${id})`} />
      <circle
        className="hw26-moonglow"
        cx="86"
        cy="13"
        r="22"
        fill={`url(#hw26-moon-glow-l${id})`}
      />
      <circle cx="86" cy="13" r="10.5" fill={`url(#hw26-moon-l${id})`} />
      <circle cx="82.5" cy="10.5" r="1.6" fill={C.glow} opacity=".35" />
      <circle cx="89" cy="16" r="1.1" fill={C.glow} opacity=".3" />
      <g fill={C.glow} opacity=".7" transform="translate(.7 -.6)">
        <path d={HOUSE} />
        <path d={TREE_L} opacity=".45" />
      </g>
      <g fill={C.ink}>
        <path d="M0 40V33Q40 28 92 31Q150 35 205 35Q255 35 300 38V40Z" />
        <path d={TREE_L} />
        <path d={HOUSE} />
        <path d="M104 40V34.5H106V33L107 32 108 33V34.5H111V33L112 32 113 33V34.5H116V33L117 32 118 33V34.5H121V33L122 32 123 33V34.5H126V33L127 32 128 33V34.5H130V40Z" />
      </g>
      <g fill={C.glow}>
        <rect
          className="hw26-win"
          x="62.5"
          y="27"
          width="3.5"
          height="4.5"
          rx=".6"
        />
        <rect
          className="hw26-win"
          x="71"
          y="27"
          width="3.5"
          height="4.5"
          rx=".6"
        />
        <rect
          className="hw26-win"
          x="88.5"
          y="16"
          width="3"
          height="4"
          rx=".5"
        />
        <rect
          className="hw26-win"
          x="83"
          y="27.5"
          width="3.5"
          height="5"
          rx=".6"
          opacity=".75"
        />
        <path d="M76 40V33.5Q77.8 31.2 79.6 33.5V40Z" opacity=".55" />
      </g>
      <Bat args={[118, 9, 1.05, C.ink]} />
      <Bat args={[134, 15, 0.7, C.ink]} />
      <Bat args={[149, 7, 0.55, C.ink]} />
    </svg>
  );
}

export function StripSceneRight({ className }: { className?: string }) {
  const id = useId().replace(/:/g, "");
  return (
    <svg
      className={`hw26-strip-scene hw26-strip-scene--right hw26-mask ${className ?? ""}`}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 300 40"
      preserveAspectRatio="xMaxYMax meet"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <radialGradient
          id={`hw26-sky-r${id}`}
          cx="238"
          cy="12"
          r="82"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#6B47A8" stopOpacity=".7" />
          <stop offset=".45" stopColor="#3E2470" stopOpacity=".42" />
          <stop offset="1" stopColor="#3E2470" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`hw26-moon-glow-r${id}`}>
          <stop offset="0" stopColor={C.star} stopOpacity=".5" />
          <stop offset="1" stopColor={C.star} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`hw26-moon-r${id}`} cx=".35" cy=".4" r=".8">
          <stop offset="0" stopColor="#FFF1D6" />
          <stop offset="1" stopColor="#FFC266" />
        </radialGradient>
        <mask id={`hw26-crescent${id}`}>
          <rect width="300" height="40" fill="#fff" />
          <circle cx="242" cy="9.4" r="8.4" fill="#000" />
        </mask>
      </defs>
      <ellipse
        cx="236"
        cy="18"
        rx="96"
        ry="40"
        fill={`url(#hw26-sky-r${id})`}
      />
      <circle
        className="hw26-moonglow"
        cx="237"
        cy="12"
        r="19"
        fill={`url(#hw26-moon-glow-r${id})`}
      />
      <circle
        cx="237"
        cy="12"
        r="9"
        fill={`url(#hw26-moon-r${id})`}
        mask={`url(#hw26-crescent${id})`}
      />
      <g fill={C.glow} opacity=".7" transform="translate(-.7 -.6)">
        <path d={TREE_R} />
        <path d={STONE} opacity=".6" />
        <path d={CROSS} opacity=".6" />
        <path d={CAT} opacity=".6" />
      </g>
      <g fill={C.ink}>
        <path d="M0 40V38Q60 36 120 36Q180 35 220 31Q262 27 300 32V40Z" />
        <path d={TREE_R} />
        <path d={STONE} />
        <path d={CROSS} />
        <path d={CAT} />
        <path d="M170 40V33Q170 29.6 173.8 29.6Q177.6 29.6 177.6 33V40Z" />
        <path d="M226 40L227.6 31.4Q228.4 28.4 231.4 29.2Q234.2 30 233.6 33L232 40Z" />
        <path
          d="M252.6 8.2V18.6"
          stroke={C.ink}
          strokeWidth=".55"
          fill="none"
        />
        <ellipse cx="252.6" cy="20.2" rx="1.6" ry="1.9" />
        <path
          d="M250.4 19L248.6 17.8M250.4 20.2H248.2M250.4 21.4L248.6 22.6M254.8 19L256.6 17.8M254.8 20.2H257M254.8 21.4L256.6 22.6"
          stroke={C.ink}
          strokeWidth=".6"
          fill="none"
          strokeLinecap="round"
        />
      </g>
      <Bat args={[206, 7, 1.0, C.ink]} />
      <Bat args={[190, 13, 0.7, C.ink]} />
      <Bat args={[218, 3, 0.55, C.ink]} />
    </svg>
  );
}
